import { useRef, useState, useEffect, useMemo, type MutableRefObject } from "react";
import { Form, Field } from "react-final-form";
import { Trash2, Pencil, Check, X } from "lucide-react";
import { EventComment, EventCommentItem, TeamPlayer } from "../../../types";
import { _, locale } from "i18n";
import type { Keys } from "i18n/locales/types";
import { getDateFormat } from "ui-kit/src/functions";
import { Mention, MentionsInput, SuggestionDataItem } from "react-mentions";
import { mentionHighlightStyle, mentionStyles } from "./MentionStyles";
import "./video-comments.css";
import { FormApi } from "final-form";
import { PlayerPickerPopover } from "./PlayerPickerPopover";

interface EditableCommentProps {
	comment: EventCommentItem;
	onEdit: (commentId: number, data: any) => void;
	onDelete: (commentId: number) => void;
	teamPlayers: TeamPlayer[];
	editFormRef?: MutableRefObject<FormApi<EventComment> | null>;
}

const deliveryOptions = [
	{ id: "players", label: "discover.gameComment.form.delivery.players" },
	{ id: "players_and_parents", label: "discover.gameComment.form.delivery.entireTeam" },
	{ id: "tagged_players", label: "discover.gameComment.form.taggedPlayers" },
	{
		id: "tagged_players_and_their_parents",
		label: "discover.gameComment.form.taggedPlayersAndParents",
	},
];

const MENTION_REGEX_EDIT = /@\[(.+?)\]\((\d+)\)/g;

function getTaggedIdsEdit(text: string): Set<number> {
	const ids = new Set<number>();
	const regex = new RegExp(MENTION_REGEX_EDIT.source, "g");
	let match;
	while ((match = regex.exec(text)) !== null) {
		ids.add(Number(match[2]));
	}
	return ids;
}

function insertMentionEdit(currentText: string, player: TeamPlayer): string {
	const mention = `@[${player.name}](${player.id})`;
	const separator = currentText.trim().length > 0 && !currentText.endsWith(" ") ? " " : "";
	return currentText + separator + mention + " ";
}

function removeMentionEdit(currentText: string, playerId: number): string {
	const regex = new RegExp(`@\\[([^\\]]+)\\]\\(${playerId}\\)\\s?`, "g");
	return currentText.replace(regex, "").trimEnd();
}

function formatCommentCreatedAt(isoString: string): string {
	return new Intl.DateTimeFormat(getDateFormat(locale), {
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(isoString));
}

export function EditableComment({ comment, onEdit, onDelete, teamPlayers, editFormRef }: EditableCommentProps) {
	const [isEditing, setIsEditing] = useState(false);
	const [showPlayerPicker, setShowPlayerPicker] = useState(false);
	const [currentComment, setCurrentComment] = useState<EventCommentItem>(comment);
	const formRef = useRef<FormApi<EventComment> | null>(null);

	useEffect(() => {
		setCurrentComment(comment);
	}, [comment]);

	const handleEditClick = () => {
		setIsEditing(true);
	};

	const handleSaveEdit = (values: EventComment) => {
		const updatedComment = {
			...currentComment,
			text: values.text,
			lockerRoomVisibility: values.lockerRoomVisibility,
			visibleToAll: values.visibleToAll,
		};
		setCurrentComment(updatedComment);

		onEdit(comment.id, {
			text: values.text,
			lockerRoomVisibility: values.lockerRoomVisibility,
			visibleToAll: values.visibleToAll,
		});
		setIsEditing(false);
		if (editFormRef) editFormRef.current = null;
	};

	const handleCancelEdit = () => {
		setIsEditing(false);
		if (editFormRef) editFormRef.current = null;
	};

	const handleDeleteClick = () => {
		onDelete(comment.id);
	};

	const initialValues: EventComment = {
		text: currentComment.text,
		lockerRoomVisibility: currentComment.lockerRoomVisibility,
		visibleToAll: currentComment.visibleToAll,
		setChoicesAsDefault: false,
	};

	const deliveryLabel = deliveryOptions.find((x) => x.id === currentComment.lockerRoomVisibility)?.label;

	function parseMentions(text: string): (string | JSX.Element)[] {
		const regex = /@\[(.+?)\]\((\d+)\)/g;
		const parts: (string | JSX.Element)[] = [];
		let lastIndex = 0;
		let match;

		while ((match = regex.exec(text)) !== null) {
			const [fullMatch, display, id] = match;
			const start = match.index;

			if (start > lastIndex) {
				parts.push(text.slice(lastIndex, start));
			}

			parts.push(
				<span key={id} className="text-blue-600 font-medium">
					@{display}
				</span>
			);

			lastIndex = regex.lastIndex;
		}

		if (lastIndex < text.length) {
			parts.push(text.slice(lastIndex));
		}

		return parts;
	}

	interface MentionOption extends SuggestionDataItem {
		avatar?: string | null;
	}

	const options: MentionOption[] = teamPlayers.map((player) => ({
		id: String(player.id),
		display: player.name,
		avatar: player.photo,
	}));

	const avatarInitials = useMemo(
		() =>
			currentComment.createdBy
				.split(" ")
				.map((name) => name.charAt(0))
				.join("")
				.toUpperCase(),
		[currentComment.createdBy]
	);

	return (
		<div>
			<div className="bg-white rounded-lg rounded-md border border-gray-300 p-3 w-full">
				<div className="flex-1 min-w-0">
					<div className="flex items-center justify-between mb-2">
						<div className="flex items-center gap-2">
							<div className="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold">
								{avatarInitials}
							</div>
							<span className="font-medium text-gray-900">{currentComment.createdBy}</span>
							<span className="text-gray-500 text-sm">
								{formatCommentCreatedAt(currentComment.createdAt)}
							</span>
						</div>
						{!isEditing && (
							<div className="flex items-center gap-2">
								{comment.canEdit && (
									<button onClick={handleEditClick} className="text-black hover:text-gray-600 transition-colors" type="button">
										<Pencil className="w-4 h-4" />
									</button>
								)}
								{comment.canDelete && (
									<button
										onClick={handleDeleteClick}
										className="text-red-400 hover:text-red-700 transition-colors"
										type="button"
									>
										<Trash2 className="w-4 h-4" />
									</button>
								)}
							</div>
						)}
					</div>

					<Form
						onSubmit={handleSaveEdit}
						initialValues={initialValues}
						render={({ handleSubmit, form, values }) => {
							formRef.current = form;
							if (editFormRef) {
								if (isEditing) editFormRef.current = form;
							}
							return (
								<form onSubmit={handleSubmit}>
									<div className="flex flex-col gap-2 mb-3">
										<div className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 rounded-md border border-gray-300 w-fit">
											<span className="gradient-text font-semibold">
												{_("game.locker_room.label")}:{" "}
												{deliveryLabel ? _(deliveryLabel as keyof Keys) : currentComment.lockerRoomVisibility}
											</span>
										</div>

										<Field name="visibleToAll" type="checkbox">
											{({ input }) => (
												isEditing ? (
													<div className="flex w-full items-center justify-between gap-4 mb-4 ps-1 pt-3">
														<span className="text-Text-primary text-size-sm leading-xsm font-medium">
															{_("discover.gameComment.form.visibilityAll.label")}
														</span>
														<button
															type="button"
															role="switch"
															aria-checked={!!input.checked}
															onClick={() => input.onChange(!input.checked)}
															className="toggle-switch"
														>
															<span className="toggle-knob" />
														</button>
													</div>
												) : input.checked ? (
													<div className="inline-flex items-center gap-1 text-xs bg-white px-2 py-1 rounded-md border border-gray-300 w-fit mb-1">
														<span className="text-gray-900">
															{_("discover.gameComment.form.visibilityAll.label")}
														</span>
													</div>
												) : null
											)}
										</Field>
									</div>

									<div>
										{isEditing ? (
											<Field name="text">
												{({ input }) => {
													const taggedIds = getTaggedIdsEdit(input.value ?? "");
													const taggedPlayers = teamPlayers.filter(p => taggedIds.has(p.id));
													return (
														<>
															<div className="relative mb-2 flex justify-end">
																<button
																	type="button"
																	onClick={() => setShowPlayerPicker((v) => !v)}
																	className="text-sm font-medium text-secondary bg-gray-200 rounded-md px-3 py-1.5"
																>
																	{_("discover.gameComment.form.tagPlayer.button")}
																</button>
																{showPlayerPicker && (
																	<PlayerPickerPopover
																		players={teamPlayers}
																		taggedPlayerIds={taggedIds}
																		onSelect={(player) => {
																			input.onChange(insertMentionEdit(input.value ?? "", player));
																			setShowPlayerPicker(false);
																		}}
																		onRemove={(player) => {
																			input.onChange(removeMentionEdit(input.value ?? "", player.id));
																			setShowPlayerPicker(false);
																		}}
																		onClose={() => setShowPlayerPicker(false)}
																	/>
																)}
															</div>

															<MentionsInput
																value={input.value ?? ""}
																onBlur={input.onBlur}
																onFocus={input.onFocus}
																onChange={(_event, newValue) => input.onChange(newValue)}
																placeholder={_("discover.gameComment.form.comment.label")}
																style={mentionStyles}
															>
																<Mention
																	trigger="@"
																	data={(search, callback) => {
																		if (!search) {
																			callback(options.slice(0, 6));
																			return;
																		}
																		const filtered = options
																			.filter((o) => o.display?.toLowerCase().includes(search.toLowerCase()))
																			.slice(0, 6);
																		callback(filtered);
																	}}
																	markup="@[__display__](__id__)"
																	displayTransform={(_id, display) => `@${display}`}
																	style={mentionHighlightStyle}
																	renderSuggestion={(entry: MentionOption, _search, highlightedDisplay, _index, focused) => (
																		<div className={`flex items-center gap-2 px-3 py-2 ${focused ? "bg-gray-100" : ""}`}>
																			{entry.avatar && (
																				<img
																					src={entry.avatar}
																					alt={entry.display}
																					className="w-6 h-6 rounded-full object-cover"
																				/>
																			)}
																			<span className="text-sm text-gray-800">{highlightedDisplay}</span>
																		</div>
																	)}
																/>
															</MentionsInput>

															<div className="flex justify-end gap-2 mt-3">
																<button
																	type="button"
																	onClick={handleCancelEdit}
																	aria-label="Cancel editing"
																	className="text-sm font-medium text-secondary rounded-md px-3 py-1.5 bg-Surface-action-neutral transition-colors"
																>
																	{_("common.action.cancel")}
																</button>
																<button
																	type="submit"
																	className="px-4 py-2 rounded-sm bg-Surface-action-primary text-size-base leading-sm font-medium text-Text-primary"
																>
																	{_("common.action.save")}
																</button>
															</div>
														</>
													);
												}}
											</Field>
										) : (
											<div className="text-black text-sm leading-relaxed">{parseMentions(currentComment.text)}</div>
										)}
									</div>
								</form>
							);
						}}
					/>
				</div>
			</div>
		</div>
	);
}
